import { useCallback, useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/Layouts/AppLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  whatsappService,
  DispatchDetail,
  DispatchListItem,
  DispatchStatus,
  DispatchRecipientStatus,
  WhatsAppEventType,
} from '@/services/whatsappService';
import { useAlert } from '@/hooks/useAlert';
import { Pagination } from '../RoleManagement/components';
import { useAuth } from '@/auth';
import { PERMISSIONS, checkAllPermissions } from '@/auth/permissions';
import { RefreshCw } from 'lucide-react';

const STATUS_LABELS: Record<DispatchStatus, string> = {
  queued: 'En cola',
  processing: 'Procesando',
  completed: 'Completado',
  completed_with_errors: 'Completado con errores',
  failed: 'Fallido',
};

const RECIPIENT_STATUS_LABELS: Record<DispatchRecipientStatus, string> = {
  queued: 'En cola',
  sent: 'Enviado',
  failed: 'Fallido',
  skipped_duplicate: 'Omitido por duplicado',
  invalid_phone: 'Teléfono inválido',
};

const EVENT_LABELS: Record<WhatsAppEventType, string> = {
  'escrituras.recordatorio_firma_dia': 'Recordatorio firma',
  'escrituras.mensaje_rapido_grupo': 'Mensaje rápido por grupo',
  'escrituras.envio_escritura': 'Envío escritura',
  'escrituras.envio_escritura_inscripta': 'Envío escritura inscripta',
  'escrituras.envio_presupuesto': 'Envío presupuesto',
  'contabilidad.envio_documento': 'Envío documento contabilidad',
  'contabilidad.recordatorio_pago': 'Recordatorio de pago pendiente',
  'otros_tramites.documentacion_lista_retiro': 'Otros trámites: documentación lista',
  'otros_tramites.envio_archivo': 'Otros trámites: envío de archivo',
  'otros_tramites.envio_factura': 'Otros trámites: envío de factura',
};

const badgeVariantByStatus = (status: DispatchStatus): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (status === 'completed') return 'default';
  if (status === 'failed') return 'destructive';
  if (status === 'completed_with_errors') return 'secondary';
  return 'outline';
};

const recipientBadgeVariant = (status: DispatchRecipientStatus): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (status === 'sent') return 'default';
  if (status === 'failed' || status === 'invalid_phone') return 'destructive';
  if (status === 'skipped_duplicate') return 'secondary';
  return 'outline';
};

const WhatsAppQueue = () => {
  const { user } = useAuth();
  const { errorAlert, successAlert, questionAlert } = useAlert();
  const canSendWhatsApp = checkAllPermissions(user?.permissions || [], [PERMISSIONS.SEND_WHATSAPP.name]);
  const [dispatches, setDispatches] = useState<DispatchListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [links, setLinks] = useState<Array<{ url: string | null; label: string; active: boolean; onClick: () => void }>>([]);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState('');
  const [eventType, setEventType] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedDispatchId, setSelectedDispatchId] = useState<number | null>(null);
  const [detail, setDetail] = useState<DispatchDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<number[]>([]);

  const fetchDispatches = useCallback(async (
    page: number = currentPage,
    overrides?: {
      status?: string;
      eventType?: string;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
    },
  ) => {
    setLoading(true);
    try {
      const nextStatus = overrides?.status ?? status;
      const nextEventType = overrides?.eventType ?? eventType;
      const nextSearch = overrides?.search ?? search;
      const nextDateFrom = overrides?.dateFrom ?? dateFrom;
      const nextDateTo = overrides?.dateTo ?? dateTo;

      const response = await whatsappService.listDispatches({
        status: (nextStatus || undefined) as DispatchStatus | undefined,
        event_type: (nextEventType || undefined) as WhatsAppEventType | undefined,
        search: nextSearch || undefined,
        date_from: nextDateFrom || undefined,
        date_to: nextDateTo || undefined,
        page,
        per_page: perPage,
      });

      setDispatches(response.data);
      setCurrentPage(response.current_page);
      setPerPage(response.per_page);
      setTotal(response.total);
      setLinks((response.links || []).map((link) => ({ ...link, onClick: () => { } })));
    } catch (error: any) {
      errorAlert({ title: 'Error al cargar cola de WhatsApp', exception: error });
    } finally {
      setLoading(false);
    }
  }, [currentPage, dateFrom, dateTo, errorAlert, eventType, perPage, search, status]);

  const fetchDispatchDetail = useCallback(async (dispatchId: number) => {
    setLoadingDetail(true);
    try {
      const response = await whatsappService.getDispatch(dispatchId);
      setDetail(response);
      if (canSendWhatsApp) {
        setSelectedRecipientIds(
          response.recipients
            .filter((recipient) => recipient.status === 'failed')
            .map((recipient) => recipient.id),
        );
      } else {
        setSelectedRecipientIds([]);
      }
    } catch (error: any) {
      errorAlert({ title: 'Error al cargar detalle del dispatch', exception: error });
    } finally {
      setLoadingDetail(false);
    }
  }, [canSendWhatsApp, errorAlert]);

  useEffect(() => {
    void fetchDispatches(1);
  }, []);

  const openDetail = async (dispatchId: number) => {
    setSelectedDispatchId(dispatchId);
    setDetailOpen(true);
    await fetchDispatchDetail(dispatchId);
  };

  const toggleRecipient = (recipientId: number) => {
    if (!canSendWhatsApp) return;
    setSelectedRecipientIds((prev) =>
      prev.includes(recipientId) ? prev.filter((id) => id !== recipientId) : [...prev, recipientId]
    );
  };

  const handleRefresh = async () => {
    await fetchDispatches(1);
    if (selectedDispatchId && detailOpen) {
      await fetchDispatchDetail(selectedDispatchId);
    }
  };

  const handleRetry = async () => {
    if (!canSendWhatsApp) {
      return;
    }

    if (!selectedDispatchId || selectedRecipientIds.length === 0) {
      errorAlert({ message: 'Selecciona al menos un destinatario para reenviar.' });
      return;
    }

    const confirmation = await questionAlert({
      title: 'Reenviar mensajes',
      text: `Se reenviará a ${selectedRecipientIds.length} destinatario(s). ¿Confirmas?`,
      icon: 'question',
      confirmButtonText: 'Reenviar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmation?.isConfirmed) return;

    setRetrying(true);
    try {
      const response = await whatsappService.retryDispatch(selectedDispatchId, selectedRecipientIds);
      successAlert({
        message: `Reenvío en cola (#${response.dispatch_id}) para ${response.totals.total} destinatario(s).`,
      });

      await fetchDispatches(currentPage);
      await fetchDispatchDetail(selectedDispatchId);
    } catch (error: any) {
      errorAlert({ title: 'Error al crear reenvío', exception: error });
    } finally {
      setRetrying(false);
    }
  };

  const paginationLinks = useMemo(() => links, [links]);

  return (
    <AppLayout title="Cola WhatsApp">
      <div className="bg-white p-4 mx-auto shadow-md rounded-md space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-2">
          <select className="h-10 rounded-md border px-3 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          <select className="h-10 rounded-md border px-3 text-sm" value={eventType} onChange={(e) => setEventType(e.target.value)}>
            <option value="">Todos los eventos</option>
            {Object.entries(EVENT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          <Input
            placeholder="Buscar por # o teléfono"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={loading || loadingDetail}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Actualizar
          </Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Evento</TableHead>
              <TableHead>Solicitado por</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Totales</TableHead>
              <TableHead>Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!loading && dispatches.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-gray-500">
                  No hay dispatches para los filtros aplicados.
                </TableCell>
              </TableRow>
            )}

            {dispatches.map((dispatch) => (
              <TableRow key={dispatch.id}>
                <TableCell>#{dispatch.id}</TableCell>
                <TableCell>{new Date(dispatch.created_at).toLocaleString()}</TableCell>
                <TableCell>{EVENT_LABELS[dispatch.event_type] || dispatch.event_type}</TableCell>
                <TableCell>{dispatch.requested_by?.name || 'Sistema'}</TableCell>
                <TableCell>
                  <Badge variant={badgeVariantByStatus(dispatch.status)}>
                    {STATUS_LABELS[dispatch.status] || dispatch.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {dispatch.totals.sent}/{dispatch.totals.total} enviados, {dispatch.totals.failed} fallidos
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" onClick={() => void openDetail(dispatch.id)}>
                    Ver detalle
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="text-sm text-gray-600 text-center">
          Mostrando {dispatches.length} de {total} dispatches
        </div>

        <Pagination
          currentPage={currentPage}
          links={paginationLinks}
          onPageChange={(page) => void fetchDispatches(page)}
          onNextPage={() => void fetchDispatches(currentPage + 1)}
          onPrevPage={() => void fetchDispatches(currentPage - 1)}
        />
      </div>

      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent side="right" className="sm:max-w-3xl w-[95%] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {detail ? `Dispatch #${detail.id}` : 'Detalle de dispatch'}
            </SheetTitle>
            <SheetDescription>
              {canSendWhatsApp
                ? 'Estado y destinatarios del envío. Selecciona recipients y dispara reenvío manual.'
                : 'Estado y destinatarios del envío en modo solo lectura.'}
            </SheetDescription>
          </SheetHeader>

          {loadingDetail || !detail ? (
            <div className="px-4 pb-4 text-sm text-gray-600">Cargando detalle...</div>
          ) : (
            <div className="px-4 pb-4 space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><strong>Evento:</strong> {EVENT_LABELS[detail.event_type] || detail.event_type}</div>
                <div><strong>Estado:</strong> {STATUS_LABELS[detail.status] || detail.status}</div>
                <div><strong>Solicitado por:</strong> {detail.requested_by?.name || 'Sistema'}</div>
                <div><strong>Fecha:</strong> {new Date(detail.created_at).toLocaleString()}</div>
              </div>

              {canSendWhatsApp && (
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={handleRetry} disabled={retrying || selectedRecipientIds.length === 0}>
                    {retrying ? 'Reenviando...' : `Reenviar seleccionados (${selectedRecipientIds.length})`}
                  </Button>
                </div>
              )}

              <Table>
                <TableHeader>
                  <TableRow>
                    {canSendWhatsApp && <TableHead></TableHead>}
                    <TableHead>Teléfono</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Intentos</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.recipients.map((recipient) => (
                    <TableRow key={recipient.id}>
                      {canSendWhatsApp && (
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedRecipientIds.includes(recipient.id)}
                            onChange={() => toggleRecipient(recipient.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>{recipient.phone_original}</TableCell>
                      <TableCell>
                        <Badge variant={recipientBadgeVariant(recipient.status)}>
                          {RECIPIENT_STATUS_LABELS[recipient.status] || recipient.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{recipient.attempts}</TableCell>
                      <TableCell>
                        <div className="max-w-xs truncate" title={recipient.error_message || ''}>
                          {recipient.error_message || '-'}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </AppLayout>
  );
};

export default WhatsAppQueue;
